'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { v4 } from 'uuid';
import { AgentLineQuote } from '@/app/components/icons/AgentLineQuote';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import type { FormattedScriptData } from '@/app/hooks';
import { useFormattedScriptData } from '@/app/hooks';
import type { AgentReferenceContent } from '@/app/types';
import { cn } from '@/app/utils';
import { CollapseMediaItemsDisplay } from './CollapseMediaItemsDisplay';

const renderScriptContent = (scriptData: FormattedScriptData) => {
  const { full_narration, copy_text } = scriptData;
  if (full_narration) {
    return copy_text;
  }
  return (
    <div className="space-y-1">
      {['full', 'full', '1/2'].map((item, _index) => (
        <div key={v4()} className={cn('h-0.5 rounded-full bg-[rgba(151,135,111,0.6)]', `w-${item}`)} />
      ))}
    </div>
  );
};

interface ReferencePreviewProps {
  /**
   * 引用内容
   */
  reference: AgentReferenceContent;
  /**
   * 删除引用回调
   */
  onRemove?: () => void;
  /**
   * 自定义类名
   */
  className?: string;
}

/**
 * 引用预览组件
 * 显示在输入框上方，展示引用的媒体内容
 */
export function ReferencePreview({ reference, onRemove, className }: ReferencePreviewProps) {
  const t = useTranslations();
  const { formatScript } = useFormattedScriptData();

  const mediaFiles = useMemo(() => {
    if (reference.type === 'script') {
      return [];
    }
    return (
      (reference.type === 'image' ? reference.images : reference.type === 'video' ? reference.videos : [])?.map(
        (item) => ({
          id: v4(),
          type: reference.type as 'image' | 'video',
          url: item.url,
          cover_url: item.cover_url,
        }),
      ) || []
    );
  }, [reference.images, reference.videos, reference.type]);

  return (
    <div
      className={cn(
        'relative flex items-center gap-3 overflow-hidden rounded-12 bg-[rgba(230,208,178,0.12)] px-2',
        className,
      )}
    >
      <div className="relative mt-2 flex h-[38px] min-w-[66px] max-w-[90px] flex-shrink-0 items-center justify-center overflow-hidden">
        {reference.type === 'script' ? (
          <div className="flex h-full w-full flex-col rounded-t-4 border border-[#A5957F] border-b-0 bg-[#C8C0B5] p-1 pb-0">
            <div className="mb-[3px] flex-shrink-0 border-[rgba(117,91,55,.16)] border-b pb-[3px] font-semibold text-[#755B37] text-[5px] leading-[1.4]">
              {t(`agentProject.contentType.${reference.type}`)}
            </div>
            <div className="flex-1 overflow-hidden text-[3px] text-[rgba(117,91,55,.64)] leading-[5px]">
              {renderScriptContent(formatScript(reference.script?.text || ''))}
            </div>
          </div>
        ) : (
          <CollapseMediaItemsDisplay
            mediaFiles={mediaFiles}
            baseTranslateX={8}
            aspectRatio={66 / 38}
            itemClassName="rounded-2 shadow-[0_2.755px_5.51px_0_rgba(0,0,0,0.24)]"
            className="h-full w-fit"
          />
        )}
      </div>

      {/* 标题 */}
      <div className="flex-1 overflow-hidden">
        <div className="truncate text-sm text-system-white/40 leading-[1.4]">
          {t(`agentProject.contentType.${reference.type}`)}
        </div>
      </div>
      <AgentLineQuote className="size-8 text-system-white08" />

      {/* 删除按钮 */}
      {onRemove && (
        <>
          <div className="h-5 w-[1px] bg-system-white08" />
          <button
            onClick={onRemove}
            className="mr-1 flex-shrink-0 text-system-white/40 transition-colors hover:text-system-white"
            title="移除引用"
          >
            <BaseLineClose className="size-4" />
          </button>
        </>
      )}
    </div>
  );
}
